/**
 * Sheet — боковая/верхняя/нижняя панель.
 * Island-layout: margin 12px, rounded-xl, border, bg-card — как navbar/main.
 */
import * as React from "react"
import { Dialog as SheetPrimitive, VisuallyHidden } from "radix-ui"
import { XIcon } from "lucide-react"

import { cn } from "@/shared/lib/cn"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/40",
        className,
      )}
      {...props}
    />
  )
}

/** Island-inset: 12px (top-3/right-3/…) — как p-3/gap-3 у AppShell. */
const islandBase =
  "bg-card text-card-foreground border border-border rounded-xl shadow-sm outline-none"

function SheetContent({
  className,
  children,
  side = "right",
  hideOverlay = false,
  hideCloseButton = false,
  title,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  hideOverlay?: boolean
  hideCloseButton?: boolean
  title?: string
}) {
  return (
    <SheetPortal>
      {!hideOverlay && <SheetOverlay />}
      <SheetPrimitive.Content
        data-slot="sheet-content"
        aria-describedby={undefined}
        className={cn(
          "data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-3 p-4 transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 sm:gap-4 sm:p-6",
          islandBase,
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right top-3 right-3 bottom-3 h-auto w-[min(100%-1.5rem,24rem)] sm:max-w-sm",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left top-3 left-3 bottom-3 h-auto w-[min(100%-1.5rem,24rem)] sm:max-w-sm",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top top-3 right-3 left-3 h-auto max-h-[min(92svh,calc(100vh-1.5rem))]",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom right-3 bottom-3 left-3 h-auto max-h-[min(92svh,calc(100vh-1.5rem))]",
          className,
        )}
        {...props}
      >
        <VisuallyHidden.Root asChild>
          <SheetPrimitive.Title>{title ?? "Панель"}</SheetPrimitive.Title>
        </VisuallyHidden.Root>
        {children}
        {!hideCloseButton && (
          <SheetPrimitive.Close className="ring-offset-background focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent absolute top-3 right-3 rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Закрыть</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 pr-8", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
